import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Button, cn } from '@workflow/ui'

/** Spreadsheet column letters: A … Z, AA, AB … */
function letters(index: number): string {
  let out = ''
  for (let value = index + 1; value > 0; value = Math.floor((value - 1) / 26)) out = String.fromCharCode(65 + ((value - 1) % 26)) + out
  return out
}

export type Align = 'left' | 'right' | 'center'

/** One cell as the grid draws it and the formula bar reads it. */
export interface Cell {
  /** What the cell shows. */
  view: ReactNode
  /** What the formula bar says about it. */
  detail: string
  /** The raw value an edit starts from; absent for cells that are not typed into. */
  edit?: { value: string; commit: (text: string) => void; locked?: string }
  tone?: 'warn' | 'muted' | 'strong'
  align?: Align
  /** Double-click does this instead of editing. */
  open?: () => void
  /** A button beside the formula bar's words. */
  action?: { label: string; run: () => void }
}

export interface Column {
  key: string
  label: ReactNode
  width: number
  align: Align
}

export interface Row {
  key: string
  number: number
  cells: Record<string, Cell>
  /** Header and total rows are drawn heavier. */
  kind: 'head' | 'body' | 'total'
}

// ---- The grid itself -----------------------------------------------------

/**
 * A row's fill, the way a kept spreadsheet reads: heading rows on a strong
 * shade (darker in light, lighter in dark), body rows banded A, B, A, B so the eye holds a line across, and
 * the total on the sunken surface. Band B is mixed opaque, so the sticky
 * first column hides what scrolls beneath it.
 */
function rowFill(entry: Row, bodyIndex: number): string {
  if (entry.kind === 'head') return 'bg-[color-mix(in_srgb,var(--color-foreground)_16%,var(--color-surface))] text-foreground'
  if (entry.kind === 'total') return 'bg-surface-sunken text-muted-foreground'
  return bodyIndex % 2 === 0
    ? 'bg-surface text-foreground'
    : 'bg-[color-mix(in_srgb,var(--color-foreground)_6%,var(--color-surface))] text-foreground'
}

export function SheetGrid({ grid, emptyMessage, label = 'Sheet', editableGrid = false }: { grid: { columns: Column[]; rows: Row[] }; emptyMessage?: string; label?: string; editableGrid?: boolean }) {
  const { columns, rows } = grid
  const [at, setAt] = useState<{ row: number; col: number }>({ row: 2, col: 0 })
  // Where the typing happens — in the cell, or in the formula bar — and what it says so far.
  const [editing, setEditing] = useState<{ text: string; where: 'cell' | 'bar' } | null>(null)
  // One edit commits once: Enter and the blur that follows it are the same decision.
  const open = useRef(false)
  const surface = useRef<HTMLDivElement>(null)
  const row = Math.min(at.row, rows.length - 1)
  const col = Math.min(at.col, columns.length - 1)
  const cell = rows[row]?.cells[columns[col]!.key]
  const editable = cell?.edit && !cell.edit.locked

  useEffect(() => {
    open.current = false
    setEditing(null)
  }, [row, col])

  const begin = (text: string, where: 'cell' | 'bar') => {
    open.current = true
    setEditing({ text, where })
  }
  const commit = (text: string) => {
    if (!open.current) return
    open.current = false
    cell?.edit?.commit(text)
    setEditing(null)
    surface.current?.focus()
  }
  const cancel = () => {
    open.current = false
    setEditing(null)
    surface.current?.focus()
  }
  const move = (dRow: number, dCol: number) =>
    setAt({ row: Math.max(0, Math.min(rows.length - 1, row + dRow)), col: Math.max(0, Math.min(columns.length - 1, col + dCol)) })

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (editing !== null) return
    const step: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1], ...(editableGrid ? { Tab: [0, event.shiftKey ? -1 : 1] as [number, number] } : {}) }
    if (step[event.key]) {
      event.preventDefault()
      move(...step[event.key]!)
    } else if (event.key === 'Enter' && editable) {
      event.preventDefault()
      begin(cell.edit!.value, 'cell')
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && editable) {
      event.preventDefault()
      cell.edit!.commit('')
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && editable) {
      event.preventDefault()
      begin(event.key, 'cell')
    }
  }

  return (
    <div className={cn('min-w-0 overflow-hidden border border-hairline bg-surface', editableGrid ? 'rounded-t-xl' : 'rounded-xl')}>
      {/* The formula bar: the selected cell's address, and what it holds. */}
      <div className="flex h-9 items-center border-b border-hairline text-caption">
        <span className="flex h-full w-16 shrink-0 items-center justify-center border-r border-hairline font-mono text-muted-foreground">
          {letters(col)}{rows[row]?.number}
        </span>
        <span className="flex h-full w-9 shrink-0 items-center justify-center border-r border-hairline font-serif text-muted-foreground italic">fx</span>
        {editable ? (
          <input
            aria-label="Cell value"
            value={editing ? editing.text : cell.edit!.value}
            onFocus={() => { if (!editing) begin(cell.edit!.value, 'bar') }}
            onChange={(event) => setEditing({ text: event.target.value, where: 'bar' })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commit(event.currentTarget.value)
              if (event.key === 'Escape') cancel()
            }}
            onBlur={(event) => commit(event.currentTarget.value)}
            className="h-full min-w-0 flex-1 bg-transparent px-3 text-foreground outline-none"
          />
        ) : (
          <span className="min-w-0 flex-1 truncate px-3 text-foreground" title={cell?.detail}>
            {cell?.edit?.locked ?? cell?.detail}
          </span>
        )}
        {cell?.action ? (
          <Button size="xs" variant="ghost" className="mr-1.5" onClick={cell.action.run}>{cell.action.label}</Button>
        ) : null}
      </div>

      <div ref={surface} tabIndex={0} onKeyDown={onKeyDown} className="overflow-x-auto outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary" aria-label={label} role="grid">
        <table className="border-separate border-spacing-0 text-caption tabular-nums" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th className="sticky left-0 z-20 h-6 w-10 min-w-10 border-r border-b border-hairline bg-surface-sunken" />
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  style={{ width: column.width, minWidth: column.width }}
                  className={cn(
                    'h-6 border-r border-b border-hairline bg-surface-sunken text-center text-[11px] font-normal text-muted-foreground',
                    index === 0 && 'sticky left-10 z-20',
                    index === col && 'text-foreground shadow-[inset_0_0_0_999px_var(--color-selected)]',
                  )}
                >
                  {letters(index)}
                </th>
              ))}
              <th className="w-full border-b border-hairline bg-surface-sunken" />
            </tr>
          </thead>
          <tbody>
            {rows.map((entry, rowIndex) => {
              const fill = rowFill(entry, rows.slice(0, rowIndex).filter((before) => before.kind === 'body').length)
              return (
              <tr key={entry.key}>
                <td
                  className={cn(
                    'sticky left-0 z-10 w-10 min-w-10 border-r border-b border-hairline bg-surface-sunken text-center text-[11px] text-muted-foreground',
                    rowIndex === row && 'text-foreground shadow-[inset_0_0_0_999px_var(--color-selected)]',
                  )}
                >
                  {entry.number}
                </td>
                {columns.map((column, colIndex) => {
                  const value = entry.cells[column.key] ?? { view: '', detail: '' }
                  const selected = rowIndex === row && colIndex === col
                  const isEditing = selected && editing?.where === 'cell'
                  return (
                    <td
                      key={column.key}
                      onMouseDown={() => setAt({ row: rowIndex, col: colIndex })}
                      onDoubleClick={() => {
                        if (value.edit && !value.edit.locked) begin(value.edit.value, 'cell')
                        else value.open?.()
                      }}
                      className={cn(
                        'relative h-8 max-w-0 border-r border-b border-hairline px-2 whitespace-nowrap',
                        (value.align ?? column.align) === 'right' ? 'text-right' : (value.align ?? column.align) === 'center' ? 'text-center' : 'text-left',
                        fill,
                        entry.kind === 'head' && 'h-9 font-medium',
                        entry.kind === 'total' && 'font-medium text-foreground',
                        value.tone === 'warn' && 'bg-status-amber/10',
                        value.tone === 'muted' && 'text-muted-foreground',
                        value.tone === 'strong' && 'font-medium text-foreground',
                        colIndex === 0 && 'sticky left-10 z-10',
                        selected && 'shadow-[inset_0_0_0_2px_var(--color-primary)]',
                      )}
                    >
                      {isEditing ? (
                        <input
                          autoFocus
                          aria-label="Edit cell"
                          value={editing.text}
                          onChange={(event) => setEditing({ text: event.target.value, where: 'cell' })}
                          onKeyDown={(event) => {
                            event.stopPropagation()
                            if (event.key === 'Enter' || event.key === 'Tab') {
                              event.preventDefault()
                              commit(event.currentTarget.value)
                              move(event.key === 'Tab' ? 0 : 1, event.key === 'Tab' ? 1 : 0)
                            }
                            if (event.key === 'Escape') cancel()
                          }}
                          onBlur={(event) => commit(event.currentTarget.value)}
                          className={cn('absolute inset-0 bg-surface px-2 text-foreground shadow-[inset_0_0_0_2px_var(--color-primary)] outline-none', column.align === 'right' && 'text-right')}
                        />
                      ) : (
                        <span className="block truncate">{value.view}</span>
                      )}
                    </td>
                  )
                })}
                <td className={cn('border-b border-hairline', fill)} />
              </tr>
              )
            })}
          </tbody>
        </table>
        {emptyMessage ? (
          <p className="px-4 py-6 text-center text-caption text-muted-foreground">
            {emptyMessage}
          </p>
        ) : null}
      </div>
    </div>
  )
}
